import React, { useState } from 'react';
import { Card, CardContent } from '../components/Card';
import { Input } from '../components/Input';
import { Button } from '../components/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/Table';
import { Badge } from '../components/Badge';
import { Avatar, AvatarFallback } from '../components/Avatar';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/Sheet';
import { ScrollArea } from '../components/ScrollArea';
import { Search, Plus, Eye, Edit, FileText, Activity, Calendar, User, FileSearch, ArrowRight, Check } from 'lucide-react';
const mockData = [{
  id: '1',
  name: 'William George',
  initials: 'WG',
  admission: 'URGENT',
  date: '12/03/2026',
  sources: 'Lab + Radiology + Notes',
  status: 'Ready'
}, {
  id: '2',
  name: 'Joan Smith',
  initials: 'JS',
  admission: 'EW EMER',
  date: '11/03/2026',
  sources: 'Notes + Vitals',
  status: 'Under Review'
}, {
  id: '3',
  name: 'Mary Williams',
  initials: 'MW',
  admission: 'OBSRV',
  date: '10/03/2026',
  sources: 'Lab + Notes',
  status: 'Draft'
}, {
  id: '4',
  name: 'James Brown',
  initials: 'JB',
  admission: 'URGENT',
  date: '09/03/2026',
  sources: 'Radiology + Notes',
  status: 'Ready'
}, {
  id: '5',
  name: 'Patricia Taylor',
  initials: 'PT',
  admission: 'EW EMER',
  date: '08/03/2026',
  sources: 'Lab + Radiology + Notes',
  status: 'Ready'
}, {
  id: '6',
  name: 'Robert Miller',
  initials: 'RM',
  admission: 'OBSRV',
  date: '07/03/2026',
  sources: 'Notes + Vitals',
  status: 'Under Review'
}, {
  id: '7',
  name: 'Linda Davis',
  initials: 'LD',
  admission: 'URGENT',
  date: '06/03/2026',
  sources: 'Lab + Notes',
  status: 'Ready'
}, {
  id: '8',
  name: 'Michael Wilson',
  initials: 'MW',
  admission: 'EW EMER',
  date: '05/03/2026',
  sources: 'Radiology + Notes',
  status: 'Draft'
}];
const getStatusBadgeVariant = (status: string) => {
  switch (status) {
    case 'Ready':
      return 'default';
    // We'll style this green in the className
    case 'Under Review':
      return 'secondary';
    // We'll style this amber
    case 'Draft':
      return 'outline';
    default:
      return 'default';
  }
};
const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case 'Ready':
      return 'bg-green-100 text-green-800 hover:bg-green-200 border-green-200';
    case 'Under Review':
      return 'bg-amber-100 text-amber-800 hover:bg-amber-200 border-amber-200';
    case 'Draft':
      return 'bg-gray-100 text-gray-800 hover:bg-gray-200 border-gray-200';
    default:
      return '';
  }
};
export function DischargeSummaries() {
  const [selectedPatient, setSelectedPatient] = useState<(typeof mockData)[0] | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const handleRowClick = (patient: (typeof mockData)[0]) => {
    setSelectedPatient(patient);
    setIsSheetOpen(true);
  };
  return <div className="p-4 lg:p-6 max-w-[1600px] mx-auto space-y-4" data-id="element-500">
      <div data-id="element-501">
        <h1 className="font-heading text-2xl sm:text-3xl font-normal text-foreground mb-1" data-id="element-502">
          Discharge Summaries
        </h1>
        <p className="text-muted-foreground text-sm" data-id="element-503">
          AI-generated summaries from EMR data
        </p>
      </div>

      <div className="flex justify-between items-center gap-4" data-id="element-504">
        <div className="relative flex-1 max-w-md" data-id="element-505">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" data-id="element-506" />
          <Input placeholder="Search by patient name, ID..." className="pl-9 bg-card" data-id="element-507" />
        </div>
        <Button className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2" data-id="element-508">
          <Plus className="w-4 h-4" data-id="element-509" />
          Generate New Summary
        </Button>
      </div>

      <Card data-id="element-510">
        <Table data-id="element-511">
          <TableHeader data-id="element-512">
            <TableRow data-id="element-513">
              <TableHead data-id="element-514">Patient Name</TableHead>
              <TableHead data-id="element-515">Admission Type</TableHead>
              <TableHead data-id="element-516">Discharge Date</TableHead>
              <TableHead data-id="element-517">EMR Sources Pulled</TableHead>
              <TableHead data-id="element-518">Summary Status</TableHead>
              <TableHead className="text-right" data-id="element-519">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-id="element-520">
            {mockData.map(row => <TableRow key={row.id} className="cursor-pointer hover:bg-secondary/50" onClick={() => handleRowClick(row)} data-id="element-521">
                <TableCell className="font-medium" data-id="element-522">
                  <div className="flex items-center gap-3" data-id="element-523">
                    <Avatar className="h-8 w-8 border border-border" data-id="element-524">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs" data-id="element-525">
                        {row.initials}
                      </AvatarFallback>
                    </Avatar>
                    {row.name}
                  </div>
                </TableCell>
                <TableCell data-id="element-526">
                  <Badge variant="outline" className="font-mono text-xs" data-id="element-527">
                    {row.admission}
                  </Badge>
                </TableCell>
                <TableCell data-id="element-528">{row.date}</TableCell>
                <TableCell className="text-muted-foreground" data-id="element-529">
                  {row.sources}
                </TableCell>
                <TableCell data-id="element-530">
                  <Badge variant={getStatusBadgeVariant(row.status)} className={getStatusBadgeClass(row.status)} data-id="element-531">
                    {row.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right" data-id="element-532">
                  <div className="flex justify-end gap-2" data-id="element-533">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" data-id="element-534">
                      <Eye className="w-4 h-4" data-id="element-535" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" data-id="element-536">
                      <Edit className="w-4 h-4" data-id="element-537" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>)}
          </TableBody>
        </Table>
      </Card>

      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen} data-id="element-538">
        <SheetContent side="right" className="w-[800px] sm:max-w-none p-0 flex flex-col bg-background" data-id="element-539">
          {selectedPatient && <>
              {/* Header */}
              <div className="p-6 border-b border-border bg-card" data-id="element-540">
                <div className="flex justify-between items-start mb-4" data-id="element-541">
                  <div data-id="element-542">
                    <SheetTitle className="font-heading text-2xl" data-id="element-543">
                      {selectedPatient.name}
                    </SheetTitle>
                    <SheetDescription data-id="element-544">
                      MRN: 987654321 • Encounter: ENC-2026-03-12
                    </SheetDescription>
                  </div>
                  <Badge variant={getStatusBadgeVariant(selectedPatient.status)} className={getStatusBadgeClass(selectedPatient.status)} data-id="element-545">
                    {selectedPatient.status}
                  </Badge>
                </div>

                {/* Demographics Summary */}
                <div className="grid grid-cols-4 gap-4 mt-6" data-id="element-546">
                  <div className="flex items-center gap-2 text-sm" data-id="element-547">
                    <User className="w-4 h-4 text-muted-foreground" data-id="element-548" />
                    <span className="text-muted-foreground" data-id="element-549">Age/Gender:</span>
                    <span className="font-medium" data-id="element-550">68 M</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm" data-id="element-551">
                    <Activity className="w-4 h-4 text-muted-foreground" data-id="element-552" />
                    <span className="text-muted-foreground" data-id="element-553">Admission:</span>
                    <span className="font-medium" data-id="element-554">
                      {selectedPatient.admission}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm" data-id="element-555">
                    <Calendar className="w-4 h-4 text-muted-foreground" data-id="element-556" />
                    <span className="text-muted-foreground" data-id="element-557">Admitted:</span>
                    <span className="font-medium" data-id="element-558">08/03/2026</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm" data-id="element-559">
                    <Calendar className="w-4 h-4 text-muted-foreground" data-id="element-560" />
                    <span className="text-muted-foreground" data-id="element-561">Discharged:</span>
                    <span className="font-medium" data-id="element-562">{selectedPatient.date}</span>
                  </div>
                </div>
              </div>

              {/* Main Content Area */}
              <div className="flex flex-1 overflow-hidden" data-id="element-563">
                {/* Sidebar: EMR Sources */}
                <div className="w-64 border-r border-border bg-card/50 p-4 flex flex-col gap-4" data-id="element-564">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider" data-id="element-565">
                    EMR Sources
                  </h3>
                  <div className="space-y-2" data-id="element-566">
                    <div className="flex items-center gap-2 p-2 rounded-md bg-secondary text-sm font-medium border border-border" data-id="element-567">
                      <FileText className="w-4 h-4 text-primary" data-id="element-568" />
                      Discharge Summary
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded-md hover:bg-secondary/50 text-sm text-muted-foreground cursor-pointer transition-colors" data-id="element-569">
                      <FileSearch className="w-4 h-4" data-id="element-570" />
                      Radiology Reports (x5)
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded-md hover:bg-secondary/50 text-sm text-muted-foreground cursor-pointer transition-colors" data-id="element-571">
                      <FileSearch className="w-4 h-4" data-id="element-572" />
                      Urine Culture (x2)
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded-md hover:bg-secondary/50 text-sm text-muted-foreground cursor-pointer transition-colors" data-id="element-573">
                      <FileSearch className="w-4 h-4" data-id="element-574" />
                      Swab results
                    </div>
                  </div>
                </div>

                {/* Main Content: Summary Text */}
                <ScrollArea className="flex-1 p-6 bg-card" data-id="element-575">
                  <div className="prose prose-sm max-w-none" data-id="element-576">
                    <h3 className="font-heading text-lg font-medium mb-2" data-id="element-577">
                      Presenting Complaint
                    </h3>
                    <p className="text-muted-foreground mb-6" data-id="element-578">
                      Patient presented to the emergency department with a 2-day
                      history of worsening shortness of breath, productive cough
                      with yellow-green sputum, and subjective fevers. He
                      reported right-sided pleuritic chest pain. No hemoptysis,
                      no recent travel, no known sick contacts.
                    </p>

                    <h3 className="font-heading text-lg font-medium mb-2" data-id="element-579">
                      History of Present Illness
                    </h3>
                    <p className="text-muted-foreground mb-6" data-id="element-580">
                      68-year-old male with a past medical history significant
                      for COPD (GOLD stage II), hypertension, and type 2
                      diabetes mellitus. He uses a daily fluticasone/salmeterol
                      inhaler and albuterol as needed. He noticed increased use
                      of his rescue inhaler over the past week prior to
                      admission.
                    </p>

                    <h3 className="font-heading text-lg font-medium mb-2" data-id="element-581">
                      Hospital Course
                    </h3>
                    <p className="text-muted-foreground mb-6" data-id="element-582">
                      Upon admission, patient was hypoxic to 88% on room air,
                      requiring 2L nasal cannula to maintain saturations {'>'}{' '}
                      92%. Chest X-ray revealed a right lower lobe infiltrate
                      consistent with pneumonia. He was started on empiric IV
                      ceftriaxone and azithromycin. Sputum cultures eventually
                      grew Streptococcus pneumoniae, sensitive to ceftriaxone.
                      He was transitioned to oral levofloxacin on hospital day 3
                      as his oxygen requirements decreased. By hospital day 4,
                      he was saturating well on room air and deemed stable for
                      discharge.
                    </p>

                    <h3 className="font-heading text-lg font-medium mb-2" data-id="element-583">
                      Discharge Diagnoses
                    </h3>
                    <ul className="list-disc pl-5 text-muted-foreground mb-6 space-y-1" data-id="element-584">
                      <li data-id="element-585">Community-acquired pneumonia (Right lower lobe)</li>
                      <li data-id="element-586">Acute exacerbation of COPD</li>
                      <li data-id="element-587">Hypertension (stable)</li>
                      <li data-id="element-588">Type 2 Diabetes Mellitus (stable)</li>
                    </ul>

                    <h3 className="font-heading text-lg font-medium mb-2" data-id="element-589">
                      Discharge Medications
                    </h3>
                    <ul className="list-disc pl-5 text-muted-foreground mb-6 space-y-1" data-id="element-590">
                      <li data-id="element-591">Levofloxacin 500mg PO daily for 5 more days</li>
                      <li data-id="element-592">Prednisone 40mg PO daily for 5 days</li>
                      <li data-id="element-593">
                        Continue home medications: Metformin, Lisinopril, Advair
                      </li>
                    </ul>
                  </div>
                </ScrollArea>
              </div>

              {/* Footer CTA */}
              <div className="p-4 border-t border-border bg-card flex justify-end gap-3" data-id="element-594">
                <Button variant="outline" className="gap-2 px-6" data-id="element-595">
                  <Edit className="w-4 h-4" data-id="element-596" />
                  Edit Summary
                </Button>
                <Button className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2 px-6" data-id="element-597">
                  <Check className="w-4 h-4" data-id="element-598" />
                  Approve Summary
                </Button>
              </div>
            </>}
        </SheetContent>
      </Sheet>
    </div>;
}